/* eslint-disable react/jsx-props-no-spreading */
import {
    Dispatch,
    ReactElement,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
} from "react"
import { FormProvider, UseFormReturn } from "react-hook-form"

import {
    METER_NUMBER_KW,
    METER_NUMBER_LABEL,
} from "../../../../../constants/generalConstants"
import { useGetUserData } from "../../../../../hooks/useGetUserData"
import useSubmitDraft from "../../../../../services/middleware/hooks/draftApplication/useSubmitDraft"
import useGetMyOrg from "../../../../../services/middleware/hooks/myOrganisation/useGetMyOrg"
import { IFormData } from "../../../../../services/middleware/types/amendApplication/launchAmendmentTypes"
import { IFormProperties } from "../../../../../services/middleware/types/singleApplication/getSchemaTypes"
import { getValue } from "../../../../../utils/general/generalUtils"
import {
    prepareFormProperties,
    processUserAccountDerivedInputs,
    scrollToTop,
} from "../../../../../utils/submitApplication/submitApplicationUtils"
import FormErrors from "../../../../formErrors/formErrors"
import { ApplicationContext } from "../../applicationProvider"
import InputGenerator from "../../inputGenerator/inputGenerator"
import {
    DERIVED_INPUT,
    FIXED_INPUT,
    USER_ACCOUNT_DERIVED_INPUT,
} from "../../recognisedInputs/recognisedInputKeys"
import BackNextButtons from "../backNextButtons"
import { useFormGenerator } from "../useFormGenerator"

interface IBasicFormGeneratorProps {
    formKey: string
    formProperties: IFormProperties
    formData: IFormData
    isFirstStep: boolean
    setFormData: Dispatch<SetStateAction<IFormData>>
    setCurrentStepIndex: Dispatch<SetStateAction<number>>
    onClickNextSideEffect?: (methods: UseFormReturn) => void
    onClickBackFirstStep?: () => void
    firstStepNameOverride?: string
    nextDisabled?: boolean
    isAmendment: boolean
}

/**
 * Dynamically renders a form using an input schema. The form is uncontrolled
 * and uses react-hook-form to validate the inputs. Form output is "watched"
 * by react hook form, producing a JSON object of with keys aligned to to
 * form spec (API) input naming convention. This component is used to render
 * base forms dynamically. It does not render recognised forms.
 *
 * This component is context agnostic and should be used in a context-aware
 * component.
 */
const BaseFormGenerator = ({
    formKey,
    formProperties,
    formData,
    isFirstStep,
    setFormData,
    setCurrentStepIndex,
    onClickNextSideEffect,
    onClickBackFirstStep,
    isAmendment,
    firstStepNameOverride = undefined,
    nextDisabled = false,
}: IBasicFormGeneratorProps): ReactElement => {
    const { currentStepIndex } = useContext(ApplicationContext)
    const { email, firstName, lastName } = useGetUserData()
    const { postDraftApplication } = useSubmitDraft()
    const { data: orgData } = useGetMyOrg()

    const prepareInitialValues = (): {
        [x: string]: string | number | boolean | undefined
    } => {
        const baseInitialValues = formData?.[formKey] as {
            [x: string]: string | number | boolean | undefined
        }

        if (formKey === "installerCustomerDetails" && orgData?.organisation) {
            const {
                organisationName,
                organisationContactNumber,
                installerIndividualPhoneNumber,
            } = orgData.organisation

            return {
                ...baseInitialValues,
                ...(!baseInitialValues?.installerName &&
                    organisationName && { installerName: organisationName }),
                ...(!baseInitialValues?.installerCompany &&
                    organisationName && { installerCompany: organisationName }),
                ...(!baseInitialValues?.installerIndividualPhoneNumber &&
                    installerIndividualPhoneNumber && {
                        installerPhone: installerIndividualPhoneNumber,
                    }),
                ...(!baseInitialValues?.installerPhone &&
                    organisationContactNumber &&
                    !!installerIndividualPhoneNumber && {
                        installerPhone: organisationContactNumber,
                    }),
            }
        }

        return baseInitialValues
    }

    const initialValues = prepareInitialValues()

    const { errors, ...methods } =
        useFormGenerator<typeof initialValues>(initialValues)

    useEffect(() => {
        if (formKey === "installerCustomerDetails" && orgData?.organisation) {
            const {
                organisationName,
                organisationContactNumber,
                installerIndividualPhoneNumber,
            } = orgData.organisation

            const currentValues = methods.getValues()
            if (!currentValues.installerName && organisationName) {
                methods.setValue("installerName", organisationName)
            }
            if (!currentValues.installerCompany && organisationName) {
                methods.setValue("installerCompany", organisationName)
            }
            if (
                !currentValues.installerPhone &&
                installerIndividualPhoneNumber
            ) {
                methods.setValue(
                    "installerPhone",
                    installerIndividualPhoneNumber
                )
            } else if (
                !currentValues.installerPhone &&
                organisationContactNumber &&
                !installerIndividualPhoneNumber
            ) {
                methods.setValue("installerPhone", organisationContactNumber)
            }

            if (
                !currentValues.installerIndividualPhoneNumber &&
                installerIndividualPhoneNumber
            ) {
                methods.setValue(
                    "installerIndividualPhoneNumber",
                    installerIndividualPhoneNumber
                )
            }
        }
    }, [formKey, orgData, methods])

    const processFormValues = useCallback(
        (
            formWatch: Record<string, unknown>
        ): { [x: string]: unknown | undefined } =>
            Object.entries(formProperties).reduce<{
                [x: string]: unknown | undefined
            }>((acc, [key, value]) => {
                const inputType = value.json_schema_extra.type
                if (inputType === FIXED_INPUT) {
                    return {
                        ...acc,
                        [key]: value.json_schema_extra.fixedValue ?? "",
                    }
                }

                if (inputType === USER_ACCOUNT_DERIVED_INPUT) {
                    return processUserAccountDerivedInputs(
                        acc,
                        key,
                        value,
                        email,
                        firstName,
                        lastName
                    )
                }

                if (inputType === DERIVED_INPUT) {
                    const sourceValue =
                        formWatch[
                            value.json_schema_extra.derivedValue?.source ?? ""
                        ] ?? ""
                    return {
                        ...acc,
                        [key]:
                            value.json_schema_extra.derivedValue
                                ?.sourceOutputMappings[sourceValue as string] ??
                            null,
                    }
                }
                return acc
            }, formWatch),
        [formProperties, email, firstName, lastName]
    )

    useEffect(() => {
        const subscription = methods.watch((formValues) => {
            if (formValues && Object.keys(formValues).length > 0) {
                const formWatch = JSON.parse(JSON.stringify(formValues))
                const processedValues = processFormValues(formWatch)

                const currentFormData = {
                    ...formData,
                    [formKey]: processedValues,
                }

                if (formData.installerCustomerDetails) {
                    currentFormData.installerCustomerDetails =
                        formData.installerCustomerDetails
                }

                setFormData(currentFormData)
            }
        })

        return () => {
            subscription.unsubscribe()
        }
    }, [methods, formData, formKey, processFormValues, setFormData])

    const onClickNext = async (): Promise<void> => {
        const formWatch = JSON.parse(JSON.stringify(methods.getValues()))
        const processedValues = processFormValues(formWatch)

        const updatedFormData = {
            ...formData,
            [formKey]: processedValues,
        }

        if (
            formData.installerCustomerDetails &&
            formKey !== "installerCustomerDetails"
        ) {
            updatedFormData.installerCustomerDetails =
                formData.installerCustomerDetails
        }

        // Email validation
        if (formKey === "installerCustomerDetails") {
            const installerEmail = email?.trim().toLowerCase()

            const customerEmail = Object.entries(processedValues)
                .find(([key]) => key.toLowerCase().includes("email"))?.[1]
                ?.toString()
                .trim()
                .toLowerCase()

            if (
                installerEmail &&
                customerEmail &&
                installerEmail === customerEmail
            ) {
                methods.setError("customerEmail", {
                    type: "manual",
                    message:
                        "Customer email cannot be the same as installer email",
                })

                return
            }
        }

        setFormData(updatedFormData)

        scrollToTop(document)
        setCurrentStepIndex((prev) => prev + 1)

        if (updatedFormData.installerCustomerDetails) {
            postDraftApplication(false, updatedFormData, currentStepIndex + 1)
        }

        if (onClickNextSideEffect) {
            onClickNextSideEffect(methods)
        }
    }

    return (
        <FormProvider {...methods}>
            {prepareFormProperties(formProperties).map(([key, value]) => {
                // Only disable specific address fields during amendments
                const shouldDisableAddressFields =
                    formKey === "installerCustomerDetails" &&
                    (key === "mainAddress" || key === "installationPostCode") &&
                    isAmendment &&
                    value.json_schema_extra.disableDuringAmendment

                // Only apply disableDuringAmendment logic if it's actually an amendment
                const shouldDisableField = isAmendment
                    ? shouldDisableAddressFields ||
                      value.json_schema_extra.disableDuringAmendment
                    : false

                return (
                    <InputGenerator<string | number | boolean | undefined>
                        key={key}
                        isAmendment={isAmendment}
                        registerName={key}
                        inputType={value.json_schema_extra.type}
                        inputSpec={{
                            ...value,
                            json_schema_extra: {
                                ...value.json_schema_extra,
                                disableDuringAmendment: shouldDisableField,
                            },
                        }}
                        watch={(k) => methods.watch(k)}
                        programmaticallySetForm={(k, v) =>
                            methods.setValue(k, v)
                        }
                    />
                )
            })}
            <FormErrors
                errors={errors}
                getFriendlyName={(key: string) =>
                    getValue(
                        {
                            ...formProperties,
                            [METER_NUMBER_KW]: { title: METER_NUMBER_LABEL },
                        },
                        key
                    )?.title ?? key
                }
            />
            <BackNextButtons
                onClickNext={() => {
                    methods.handleSubmit(onClickNext)()
                }}
                setCurrentStepIndex={setCurrentStepIndex}
                isFirstStep={isFirstStep}
                onClickBackFirstStep={onClickBackFirstStep}
                firstStepNameOverride={firstStepNameOverride}
                nextDisabled={nextDisabled}
            />
        </FormProvider>
    )
}

BaseFormGenerator.defaultProps = {
    onClickNextSideEffect: undefined,
    onClickBackFirstStep: undefined,
    firstStepNameOverride: undefined,
}

export default BaseFormGenerator
